import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiClock,FiShield,FiUsers,FiLock,FiEye,FiCheckCircle}=FiIcons;

const InfoSection=()=> {
  const infoCards=[
    {icon: FiClock,title: "24/7 Überwachung",description: "Unser Team überwacht rund um die Uhr alle gemeldeten Vorfälle.",color: "from-blue-500 to-cyan-500"},
    {icon: FiShield,title: "Schnelle Reaktion",description: "Kritische Sicherheitsvorfälle werden innerhalb von 1 Stunde bearbeitet.",color: "from-green-500 to-emerald-500"},
    {icon: FiLock,title: "Datenschutz",description: "Alle Berichte werden vertraulich behandelt und DSGVO-konform verarbeitet.",color: "from-purple-500 to-pink-500"},
    {icon: FiUsers,title: "Expertenteam",description: "Zertifizierte Fachleute analysieren jeden einzelnen Fall sorgfältig.",color: "from-orange-500 to-red-500"}
  ];

  const steps=[
    {step: "01",title: "Bericht einreichen",description: "Füllen Sie das Formular mit allen verfügbaren Informationen aus."},
    {step: "02",title: "Analyse & Prüfung",description: "Unser Team analysiert den Vorfall und leitet erste Maßnahmen ein."},
    {step: "03",title: "Maßnahmen ergreifen",description: "Wir blockieren schädliche Inhalte und informieren betroffene Nutzer."},
    {step: "04",title: "Rückmeldung",description: "Sie erhalten eine Bestätigung über die ergriffenen Maßnahmen."}
  ];

  return (
    <section className="py-20 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Info Cards */}
        <motion.div 
          initial={{opacity: 0,y: 50}}
          whileInView={{opacity: 1,y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8}}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent mb-4">
              Warum Malerbetrieb Pelzer?
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Vertrauen Sie auf unsere Erfahrung und professionellen Service
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infoCards.map((card,index)=> (
              <motion.div 
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6,delay: index * 0.1}}
                whileHover={{y: -10,scale: 1.02}}
                className="bg-black/30 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center group"
              >
                <motion.div 
                  whileHover={{rotate: 360}}
                  transition={{duration: 0.6}}
                  className={`bg-gradient-to-r ${card.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}
                >
                  <SafeIcon icon={card.icon} className="w-8 h-8 text-white" />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {card.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {card.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Steps */}
        <motion.div 
          initial={{opacity: 0,y: 50}}
          whileInView={{opacity: 1,y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8}}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-4">
              Unser Prozess
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              So gehen wir mit Ihrem Bericht um - transparent und effizient
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step,index)=> (
              <motion.div 
                key={index}
                initial={{opacity: 0,scale: 0.8}}
                whileInView={{opacity: 1,scale: 1}}
                viewport={{once: true}}
                transition={{duration: 0.6,delay: index * 0.2}}
                className="relative"
              >
                {/* Connection line */}
                {index < steps.length - 1 && (
                  <motion.div 
                    initial={{scaleX: 0}}
                    whileInView={{scaleX: 1}}
                    viewport={{once: true}}
                    transition={{duration: 0.8,delay: index * 0.2 + 0.5}}
                    className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-cyan-500 to-purple-500 transform origin-left"
                  />
                )}
                <div className="text-center">
                  <motion.div 
                    whileHover={{scale: 1.1}}
                    className="bg-gradient-to-r from-cyan-500 to-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 relative"
                  >
                    <span className="text-white font-bold text-lg">{step.step}</span>
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full opacity-0"
                      whileHover={{opacity: 0.3,scale: 1.2}}
                      transition={{duration: 0.3}}
                    />
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Additional Information Section */}
        <motion.div 
          initial={{opacity: 0,y: 50}}
          whileInView={{opacity: 1,y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8,delay: 0.3}}
          className="mt-20"
        >
          <div className="bg-black/30 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent mb-8 text-center">
              Zweck dieser Seite
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white">Welche Vorfälle gemeldet werden können</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Spam oder unerwünschte Kommunikation</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Phishing-Versuche</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Verteilung von Malware</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Hacking- oder Einbruchsversuche</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Brute-Force-Attacken</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Botnet-Aktivitäten</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Urheberrechtsverletzungen</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Andere Formen von Netzwerk- oder Systemmissbrauch</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white">Welche Informationen wir benötigen</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span>Zeitpunkt des Vorfalls (bevorzugt in UTC)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span>Betroffene IP-Adresse, Domain oder URL</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span>Relevante Logdaten oder technische Nachweise</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span>Kurze Beschreibung des Vorfalls</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span>Optional: Kontaktangaben für Rückfragen</span>
                  </li>
                </ul>

                <div className="mt-8 p-6 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-2xl">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-orange-400" />
                    <span>Bearbeitungszeiten</span>
                  </h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Eingangsbestätigung innerhalb von 24 Stunden</li>
                    <li>• Kritische Vorfälle werden priorisiert behandelt</li>
                    <li>• Bearbeitungsdauer hängt von Fallkomplexität ab</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl">
              <h4 className="text-lg font-semibold text-white mb-3">Wichtige Hinweise</h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Diese Kontaktadresse ist ausschließlich für Missbrauchsmeldungen gedacht</li>
                <li>• Bitte übermitteln Sie keine sensiblen oder unnötigen personenbezogenen Daten</li>
                <li>• Meldungen ohne relevanten Bezug zu unseren Systemen werden nicht weiterverfolgt</li>
                <li>• Unser Unternehmen folgt gängigen Sicherheits- und Branchenstandards</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InfoSection;